import React, { useEffect, useMemo } from 'react';
import SFNavPanel, {
  SFNavPanelProps
} from '../../../Components/SFNavPanel/SFNavPanel';
import {
  isPlanConnect,
  checkPermissions,
  isRoleOfficer,
  isRoleWatcher,
  User,
  SubscriptionsResume
} from 'ui-smartforce-settings';
import { SFNavLink } from '../../../Components/SFNavPanel/SFNavLink';
import {
  AGENCY_REPORTS_READ,
  AGENCY_REPORTS_CREATE,
  USER_REPORTS_READ,
  AGENCY_REPORTS_COLLABORATION_READ
} from '../../../Constants/RolesAndPermissions';
import { useHistory, useLocation } from 'react-router-dom';

export interface MainNavMenuProps
  extends Omit<SFNavPanelProps, 'links' | 'bottomLinks'> {
  user: User;
  subcriptionsResume: SubscriptionsResume;
  isPhone: boolean;
  onToursClick: () => void;
  onFeedbackClick: () => void;
  onTitleChange: (title: string) => void;
}

export const MainNavMenu = ({
  user,
  subcriptionsResume,
  isPhone,
  onToursClick,
  onFeedbackClick,
  onTitleChange,
  ...props
}: MainNavMenuProps): React.ReactElement<MainNavMenuProps> => {
  const location = useLocation();
  const history = useHistory();

  const links: SFNavLink[] = useMemo(() => {
    let myLinks: SFNavLink[] = [];

    if (user) {
      if (
        subcriptionsResume.appPlan.cc &&
        !isPlanConnect(subcriptionsResume.appPlan.cc) &&
        checkPermissions(AGENCY_REPORTS_READ, user.role?.permissions)
      ) {
        if (!subcriptionsResume.appPlan.nexus) {
          myLinks = [
            ...myLinks,
            {
              pathname: '/app/agency-reports/contacts',
              label: 'Agency Reports',
              icon: 'Users',
              onClick: () => history.push('/app/agency-reports/contacts')
            }
          ];
        } else {
          myLinks = [
            ...myLinks,
            {
              pathname: '/app/agency-reports',
              label: 'Agency Reports',
              icon: 'Users',
              onClick: () => history.push('/app/agency-reports'),
              children: [
                {
                  pathname: '/app/agency-reports/contacts',
                  label: 'Contacts',
                  onClick: () => history.push('/app/agency-reports/contacts')
                }
              ]
            }
          ];
        }
      }

      if (
        subcriptionsResume.appPlan.cc &&
        !isPlanConnect(subcriptionsResume.appPlan.cc) &&
        checkPermissions(AGENCY_REPORTS_CREATE, user.role?.permissions)
      ) {
        myLinks = [
          ...myLinks,
          {
            pathname: '/app/report',
            label: 'Create Report',
            icon: 'Add-File',
            offline: true,
            onClick: () => history.push('/app/report')
          }
        ];
      }

      if (
        subcriptionsResume.appPlan.cc &&
        !isPlanConnect(subcriptionsResume.appPlan.cc) &&
        checkPermissions(USER_REPORTS_READ, user.role?.permissions)
      ) {
        if (!subcriptionsResume.appPlan.nexus) {
          myLinks = [
            ...myLinks,
            {
              pathname: '/app/my-reports/contacts',
              label: 'My Reports',
              icon: 'Files',
              onClick: () => history.push('/app/my-reports/contacts')
            }
          ];
        } else {
          myLinks = [
            ...myLinks,
            {
              pathname: '/app/my-reports',
              label: 'My Reports',
              icon: 'Files',

              onClick: () => history.push('/app/my-reports'),
              children: [
                {
                  pathname: '/app/my-reports/contacts',
                  label: 'Contacts',
                  onClick: () => history.push('/app/my-reports/contacts')
                }
              ]
            }
          ];
        }
      }

      if (
        subcriptionsResume.appPlan.cc &&
        !isPlanConnect(subcriptionsResume.appPlan.cc) &&
        isRoleOfficer(user.role.id) &&
        checkPermissions(
          AGENCY_REPORTS_COLLABORATION_READ,
          user?.role?.permissions
        )
      ) {
        myLinks = [
          ...myLinks,
          {
            pathname: '/app/collaborated-reports',
            label: 'Collaborated Reports',
            icon: 'Share-File-2',
            onClick: () => history.push('/app/collaborated-reports')
          }
        ];
      }

      myLinks = [
        ...myLinks,
        {
          pathname: '/app/business-card',
          label: 'Business Card',
          icon: 'Name-Tag',
          offline: true,
          onClick: () => history.push('/app/business-card')
        }
      ];

      if (
        subcriptionsResume.appPlan.cc &&
        !isPlanConnect(subcriptionsResume.appPlan.cc)
      ) {
        if (!subcriptionsResume.appPlan.nexus) {
          myLinks = [
            ...myLinks,
            {
              pathname: '/app/analytics/contacts',
              label: 'Analytics',
              icon: 'Block-Chart-1',
              onClick: () => history.push('/app/analytics/contacts')
            }
          ];
        } else {
          myLinks = [
            ...myLinks,
            {
              pathname: '/app/analytics',
              label: 'Analytics',
              icon: 'Block-Chart-1',
              onClick: () => history.push('/app/analytics'),
              children: [
                {
                  pathname: '/app/analytics/contacts',
                  label: 'Contacts',
                  onClick: () => history.push('/app/analytics/contacts')
                }
              ]
            }
          ];
        }
      }
    }

    myLinks = [
      ...myLinks,
      {
        pathname: '/app/amend',
        label: 'Amend Report',
        icon: 'Pencil',
        temporal: true
      }
    ];

    return myLinks;
  }, [history, subcriptionsResume, user]);

  const bottomLinks: SFNavLink[] = useMemo(() => {
    let links: SFNavLink[] = [
      {
        pathname: '/app/settings',
        label: 'Settings',
        icon: 'Preferences-1',
        onClick: () => history.push('/app/settings')
      }
    ];

    if (
      subcriptionsResume.appPlan.cc &&
      !isPhone &&
      !isRoleWatcher(user?.role.id)
    ) {
      links = [
        ...links,
        {
          label: 'Feature Tours',
          icon: 'Rectangle-Star',
          onClick: onToursClick
        }
      ];
    }

    return [
      ...links,
      {
        label: 'Feedback',
        icon: 'Speach-Bubble',
        onClick: onFeedbackClick
      },
      {
        to: `${process.env.REACT_APP_CC_SUPPORT_URL}`,
        label: 'Support',
        icon: 'Question-Mark',
        onClick: () =>
          window.open(process.env.REACT_APP_CC_SUPPORT_URL as string, '_blank')
      }
    ];
  }, [
    history,
    isPhone,
    onFeedbackClick,
    onToursClick,
    subcriptionsResume.appPlan.cc,
    user?.role.id
  ]);

  useEffect(() => {
    const activeLink: SFNavLink | undefined = links.find(
      (link: SFNavLink) =>
        link.pathname && location.pathname.includes(link.pathname)
    );
    if (activeLink) {
      onTitleChange(activeLink.label);
    }
  }, [links, location.pathname, onTitleChange]);

  return (
    <SFNavPanel
      className={props.className}
      links={links}
      bottomLinks={bottomLinks}
      isBigScreen={props.isBigScreen}
      isOpen={props.isOpen}
      onClose={props.onClose}
      reminderProps={props.reminderProps}
    />
  );
};
